// What happened on the board in a period: the shape of the meeting brief (Claude's meeting_brief
// tool) and of the What's new page, the periods people pick, and the plain text both print.
// Pure, no Node or database imports: the server and the browser both use it. The brief itself is
// worked out by briefFrom / meetingBrief (services/queries.ts); this is what they produce.

import { redact } from "./guard.ts";
import { nameList, type ItemKind } from "./shared.ts";
import { DAY, dayLabel, dayTimeLabel, localDate, localParts, startOfDay, weekdayLabel, zonedTime } from "./time.ts";

// -- The brief ------------------------------------------------------------------------

export type BriefGroupBy = "person" | "area" | "project";

/**
 * Where an item lands, once per item, by what it came to in the period (the furthest step wins):
 * closed (a fix verified, a task done, anything closed - and still closed), deployed (marked
 * deployed and still waiting for verify), developed (marked developed and still waiting for deploy),
 * review (OEMUAP: a PR for it was opened, made ready or reviewed in the period, and it's still in PR
 * review), added (logged in the period), updated (anything else: notes, assignments, moves, sent
 * back), or stuck (open, with someone, and nothing happened to it in the period).
 */
export type BriefSection = "closed" | "deployed" | "developed" | "review" | "added" | "updated" | "stuck";

export const BRIEF_SECTIONS: { id: BriefSection; label: string; text: string; count: string; hint: string }[] = [
  { id: "closed", label: "Verified & closed", text: "Verified & closed", count: "verified", hint: "Fixes verified, tasks done and items closed in this period" },
  { id: "deployed", label: "Deployed", text: "Deployed (waiting for verify)", count: "deployed", hint: "Marked deployed in this period; waiting for someone to verify" },
  { id: "developed", label: "Developed", text: "Developed (waiting for deploy)", count: "developed", hint: "Marked developed in this period; waiting to be deployed" },
  { id: "review", label: "In PR review", text: "In PR review (waiting for a reviewer)", count: "in PR review", hint: "A pull request for it was opened, made ready or reviewed in this period; still waiting for review or merge" },
  { id: "added", label: "New items", text: "New", count: "new", hint: "Logged in this period" },
  { id: "updated", label: "Updated", text: "Updated", count: "updated", hint: "Notes, assignment changes and other moves in this period" },
  { id: "stuck", label: "Stuck", text: "Stuck (nothing happened this period)", count: "stuck", hint: "Open, with someone, and nothing happened to it in this period" },
];
export const SECTION_IDS = BRIEF_SECTIONS.map((s) => s.id);

/** One thing someone did to an item in the period. */
export interface BriefStep {
  kind: string;
  /** short, for a row: "Verified", "Deployed", "Note", "Assigned to Rohit" */
  head: string;
  /** the history line: "verified it: checked on the phone — moved to the archive" */
  text: string;
  at: string;
  who: string | null;
  /** done by their AI app (Claude, ...) */
  ai: string | null;
}

export interface BriefLine {
  num: number;
  /** how plain text names it: "01-07 (#185)", "Project 01 (#172)", "#188" */
  ref: string;
  /** its number as a board shows it outside its project: "01-07", "Project 01", "#188" */
  no: string;
  title: string;
  kind: ItemKind | null;
  /** who has it now (the lead, when several people do) */
  assignee: string | null;
  /** everyone it's with now, the lead first (OEM-285); absent in an older brief: just `assignee` */
  assignees?: string[];
  /** what happened / the next step */
  text: string;
  /** what this group did to it in the period, oldest first (none for stuck and working) */
  steps: BriefStep[];
  /** the release it was deployed in, when GitHub said (a tag, or what a deploy run built): "prod-26.10.02.1" */
  release?: string | null;
}

/**
 * Deployed lines by the release they went out in (docs/github.md): one group per release, newest
 * name last as the brief has them, then the lines with no release under null. Pure.
 */
export function byRelease<L extends Pick<BriefLine, "release">>(lines: L[]): { release: string | null; lines: L[] }[] {
  const out = new Map<string | null, L[]>();
  for (const l of lines) out.set(l.release ?? null, [...(out.get(l.release ?? null) ?? []), l]);
  return [...out.entries()].sort(([a], [b]) => (a === null ? 1 : b === null ? -1 : a.localeCompare(b, undefined, { numeric: true }))).map(([release, ls]) => ({ release, lines: ls }));
}

export interface BriefGroup {
  /** a person's name, an area label, or a project ("01 Checkout") */
  name: string;
  closed: BriefLine[];
  deployed: BriefLine[];
  developed: BriefLine[];
  /** OEMUAP: in PR review, with a PR opened, made ready or reviewed in the period */
  review: BriefLine[];
  added: BriefLine[];
  updated: BriefLine[];
  stuck: BriefLine[];
  working: BriefLine[];
  /** items archived with "archive every closed item": one line for all of them, not one each */
  bulkArchived: number[];
}

export interface BriefNote {
  who: string;
  ai: string | null;
  at: string;
  /** credentials redacted */
  body: string;
}

export interface Brief {
  since: string;
  until: string;
  groupBy: BriefGroupBy;
  groups: BriefGroup[];
  /** the first screenshot (one the credential scan cleared) of a few new or closed items */
  shots: { num: number; id: string; thumbKey: string; mime: string }[];
  /** each listed item's latest note in the period */
  notes: Record<number, BriefNote>;
  /**
   * Open items past their due date (OEM-267), the most overdue first, in the brief's scope (the main
   * board or one project), whatever happened to them in the period. `days`: how many days overdue.
   */
  overdue?: (BriefLine & { due: string; days: number })[];
}

export const emptyGroup = (name: string): BriefGroup => ({ name, closed: [], deployed: [], developed: [], review: [], added: [], updated: [], stuck: [], working: [], bulkArchived: [] });

/** A step's short head for a row ("Verified", "Assigned to Rohit"), from its history event. */
export function stepHead(kind: string, data: Record<string, unknown>): string {
  const to = typeof data.to === "string" ? data.to : null;
  switch (kind) {
    case "created":
      return "Logged";
    case "status":
      if (to === "closed") return data.kind === "task" ? "Done" : "Closed";
      return "Reopened";
    case "stage": {
      const rank = (s: unknown) => ["todo", "developed", "deployed", "verified"].indexOf(String(s));
      if (rank(data.from) > rank(to) && rank(to) >= 0) return to === "todo" ? "Sent back to To do" : `Moved back to ${to === "developed" ? "Developed" : "Deployed"}`;
      if (to === "verified") return "Verified";
      if (to === "deployed") return "Deployed";
      if (to === "developed") return "Developed";
      return "Moved";
    }
    case "archive":
      return data.value ? "Archived" : "Restored";
    case "assign":
      // several people (OEM-285): everyone it is with now
      if (Array.isArray(data.people)) return data.people.length ? `Assigned to ${nameList(data.people as string[])}` : "Unassigned";
      return to ? `Assigned to ${to}` : "Unassigned";
    case "claim":
      return "Claimed";
    case "unclaim":
      return "Given back";
    case "note":
      return "Note";
    case "nondev":
      return data.value ? "Moved to non-dev" : "Back to the developers";
    case "teamsres":
      return data.value ? "Resolved via Teams" : "Cleared resolved via Teams";
    case "edit":
      return "Edited";
    case "due":
      return data.to ? "Due date set" : "Due date cleared";
    case "work_started":
      return "Started work";
    case "progress":
      return "Progress";
    case "work_finished":
      return "Finished work";
    case "github_pr": {
      const what: Record<string, string> = {
        opened: "opened",
        linked: "linked",
        ready: "ready for review",
        draft: "back to draft",
        closed: "closed",
        reopened: "reopened",
        merged: "merged",
        unlinked: "unlinked",
        mention: "mentions it",
        review_requested: "review requested",
        review_request_removed: "review request removed",
        approved: "approved",
        changes_requested: "changes requested",
        review_dismissed: "review dismissed",
      };
      return `PR ${data.pr} ${what[String(data.action)] ?? "updated"}`;
    }
    default:
      return "Updated";
  }
}

const STEP_RANK: Record<string, number> = { Verified: 6, Done: 6, Closed: 6, Deployed: 5, Developed: 4, Logged: 3 };

/** The step a row leads with: the furthest one (verified over deployed over developed over logged), else the latest. */
export function leadStep(line: Pick<BriefLine, "steps">): BriefStep | null {
  let best: BriefStep | null = null;
  for (const s of line.steps) {
    if (!best || (STEP_RANK[s.head] ?? 1) >= (STEP_RANK[best.head] ?? 1)) best = s;
  }
  return best;
}

/** Every line of the brief in one section, with the group it is under. */
export const sectionLines = (b: Pick<Brief, "groups">, s: BriefSection) => b.groups.flatMap((group) => group[s].map((line) => ({ group: group.name, line })));

/** How many items are in each section (an item under two people counts once). */
export function briefCounts(b: Pick<Brief, "groups">): Record<BriefSection, number> {
  const out = {} as Record<BriefSection, number>;
  for (const s of SECTION_IDS) out[s] = new Set(sectionLines(b, s).map((x) => x.line.num)).size;
  return out;
}

/** The brief with only the lines `keep` says yes to; groups left with nothing are dropped. */
export function filterBrief(b: Brief, keep: (line: BriefLine) => boolean): Brief {
  const groups = b.groups
    .map((g) => {
      const out: BriefGroup = { ...emptyGroup(g.name), bulkArchived: g.bulkArchived };
      for (const s of [...SECTION_IDS, "working"] as const) out[s] = g[s].filter(keep);
      return out;
    })
    .filter((g) => SECTION_IDS.some((s) => g[s].length) || g.working.length || g.bulkArchived.length);
  const nums = new Set(groups.flatMap((g) => [...SECTION_IDS, "working" as const].flatMap((s) => g[s].map((l) => l.num))));
  return { ...b, groups, shots: b.shots.filter((s) => nums.has(s.num)), ...(b.overdue ? { overdue: b.overdue.filter(keep) } : {}) };
}

const textLines = (title: string, lines: BriefLine[]) => (lines.length ? [`  ${title}:`, ...lines.map((l) => `  - ${l.ref} ${l.title} — ${l.text}`)] : []);

/**
 * The brief's groups as plain text, credentials redacted: each group, then its sections in the
 * page's order (Working now before Stuck), then a bulk archive in one line. Claude's meeting_brief
 * wraps these lines as untrusted; the page copies them for Teams.
 */
export function briefBody(b: Pick<Brief, "groups">): string[] {
  const body: string[] = [];
  for (const g of b.groups) {
    if (body.length) body.push("");
    body.push(`${g.name}:`);
    for (const s of BRIEF_SECTIONS) {
      if (s.id === "stuck") body.push(...textLines("Working now", g.working));
      body.push(...textLines(s.text, g[s.id]));
    }
    if (g.bulkArchived.length) {
      const nums = [...g.bulkArchived].sort((a, z) => a - z).map((n) => `#${n}`);
      body.push(`  Archived the closed items at once: ${nums.length} (${nums.slice(0, 15).join(", ")}${nums.length > 15 ? ` and ${nums.length - 15} more` : ""})`);
    }
  }
  return body.map((l) => redact(l));
}

/** The brief's Overdue section as plain text (credentials redacted), or nothing when nothing is overdue. */
export function overdueBody(b: Pick<Brief, "overdue">): string[] {
  const lines = b.overdue ?? [];
  if (!lines.length) return [];
  return [`Overdue (${lines.length}):`, ...lines.map((l) => `  - ${l.ref} ${l.title} — ${l.text}`)].map((l) => redact(l));
}

/** "5 verified · 2 deployed · 8 new · 14 updated" (sections with nothing are left out). */
export function countsLine(counts: Record<BriefSection, number>): string {
  return BRIEF_SECTIONS.filter((s) => counts[s.id]).map((s) => `${counts[s.id]} ${s.count}`).join(" · ");
}

// -- Periods ------------------------------------------------------------------------------

/** What period a brief covers: the last N days, since the last working day, the last meeting, or a date. */
export type Period = { kind: "days"; days: number } | { kind: "workday" } | { kind: "meeting" } | { kind: "date"; date: string };

/** The longest period the page (and its API) covers. */
export const MAX_DAYS = 60;
/** The page's default: on a Monday it covers Friday and the weekend. */
export const DEFAULT_PERIOD: Period = { kind: "workday" };

export type PeriodPreset = "24h" | "workday" | "3d" | "7d" | "14d" | "meeting" | "date";

export const PERIOD_PRESETS: { id: PeriodPreset; label: string }[] = [
  { id: "24h", label: "Last 24 hours" },
  { id: "workday", label: "Since last working day" },
  { id: "3d", label: "Last 3 days" },
  { id: "7d", label: "Last 7 days" },
  { id: "14d", label: "Last 14 days" },
  { id: "meeting", label: "Since last meeting" },
  { id: "date", label: "Since a date…" },
];

const PRESET_DAYS: Partial<Record<PeriodPreset, number>> = { "24h": 1, "3d": 3, "7d": 7, "14d": 14 };

/** The preset a period is (null: a number of days no preset has, e.g. a ?days=30 link). */
export function presetOf(p: Period): PeriodPreset | null {
  if (p.kind === "days") return (Object.keys(PRESET_DAYS) as PeriodPreset[]).find((k) => PRESET_DAYS[k] === p.days) ?? null;
  return p.kind;
}

/** A preset as a period; "date" needs the date (YYYY-MM-DD). */
export function presetPeriod(id: PeriodPreset, date?: string): Period {
  const days = PRESET_DAYS[id];
  if (days) return { kind: "days", days };
  if (id === "date") return { kind: "date", date: date ?? "" };
  return { kind: id as "workday" | "meeting" };
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A period from a link's or a request's parameters: `days=3` (1 to 60), or `since=workday`,
 * `since=meeting` (also "last meeting"), `since=2026-10-01`. Neither: null (the caller's default).
 * Anything else: an error message.
 */
export function readPeriod(get: (key: string) => string | null | undefined): Period | null | { error: string } {
  const days = get("days")?.trim();
  const since = get("since")?.trim();
  if (days) {
    const n = Number(days);
    if (!Number.isInteger(n) || n < 1 || n > MAX_DAYS) return { error: `days must be a whole number from 1 to ${MAX_DAYS}.` };
    return { kind: "days", days: n };
  }
  if (!since) return null;
  const s = since.toLowerCase().replace(/[\s_-]+/g, " ");
  if (s === "workday" || s === "last working day" || s === "last workday") return { kind: "workday" };
  if (s === "meeting" || s === "last meeting") return { kind: "meeting" };
  const m = since.match(DATE_RE);
  if (!m) return { error: `since must be workday, meeting or a date like 2026-10-01 (it's "${since}").` };
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (d.getUTCFullYear() !== Number(m[1]) || d.getUTCMonth() !== Number(m[2]) - 1 || d.getUTCDate() !== Number(m[3])) return { error: `"${since}" isn't a date.` };
  return { kind: "date", date: since };
}

export const isPeriod = (p: Period | null | { error: string }): p is Period => !!p && !("error" in p);

/** A period as link parameters: [["days", "3"]], [["since", "workday"]], ... */
export function periodParams(p: Period): [string, string][] {
  switch (p.kind) {
    case "days":
      return [["days", String(p.days)]];
    case "date":
      return [["since", p.date]];
    default:
      return [["since", p.kind]];
  }
}

export const samePeriod = (a: Period, b: Period) => JSON.stringify(periodParams(a)) === JSON.stringify(periodParams(b));

/** The last meeting someone marked, if any. */
export interface MeetingMark {
  at: Date;
  by: string;
}

/** The start of the last working day before `now`'s (Monday-Friday, in the board's time zone): Monday -> Friday, Sunday -> Friday, Tuesday -> Monday. */
export function lastWorkingDay(now: Date, timeZone: string): Date {
  const wd = localParts(now.getTime(), timeZone).wd;
  let back = 1;
  while (back < 7) {
    const d = (wd - back + 7) % 7;
    if (d >= 1 && d <= 5) break;
    back++;
  }
  return startOfDay(now, timeZone, back);
}

/**
 * When a period starts, and how to say it ("Since last working day (Fri Oct 2)"), in the board's
 * time zone. A date is midnight there; "since the last meeting" with none marked is the last 7
 * days. `maxDays`: a period reaching further back is an error (a meeting further back is cut to it).
 */
export function resolvePeriod(p: Period, now: Date, timeZone: string, meeting: MeetingMark | null, maxDays: number | null = MAX_DAYS): { from: Date; label: string } | { error: string } {
  const floor = maxDays != null ? startOfDay(now, timeZone, maxDays) : null;
  switch (p.kind) {
    case "days":
      if (!Number.isInteger(p.days) || p.days < 1 || (maxDays != null && p.days > maxDays)) return { error: `days must be a whole number from 1 to ${maxDays ?? "any"}.` };
      return { from: new Date(now.getTime() - p.days * DAY), label: p.days === 1 ? "Last 24 hours" : `Last ${p.days} days` };
    case "workday": {
      const from = lastWorkingDay(now, timeZone);
      return { from, label: `Since last working day (${weekdayLabel(from, timeZone)})` };
    }
    case "meeting": {
      if (!meeting) return { from: new Date(now.getTime() - 7 * DAY), label: "Since the last meeting (none marked yet, so the last 7 days)" };
      if (floor && meeting.at < floor) return { from: floor, label: `Last ${maxDays} days (the last meeting, ${dayLabel(meeting.at, timeZone)}, is further back)` };
      return { from: meeting.at, label: `Since the last meeting (${dayTimeLabel(meeting.at, timeZone)}, marked by ${meeting.by})` };
    }
    case "date": {
      const m = p.date.match(DATE_RE);
      if (!m) return { error: `"${p.date}" isn't a date like 2026-10-01.` };
      const from = zonedTime(Number(m[1]), Number(m[2]), Number(m[3]), timeZone);
      if (p.date > localDate(now, timeZone)) return { error: `${p.date} is in the future.` };
      if (floor && from < floor) return { error: `${p.date} is more than ${maxDays} days ago; pick a later date.` };
      return { from, label: `Since ${weekdayLabel(from, timeZone)}` };
    }
  }
}

/** "Oct 3 – Oct 6", or "Oct 5" for one day, in the board's time zone. */
export function rangeLabel(from: Date, to: Date, timeZone: string): string {
  const a = dayLabel(from, timeZone);
  const b = dayLabel(to, timeZone);
  return a === b ? a : `${a} – ${b}`;
}

// -- The page's request --------------------------------------------------------------------

export interface NewsQuery {
  period: Period;
  groupBy: BriefGroupBy;
  /** one project's work (its # number); default the main board */
  project: number | null;
}

/** GET /api/whatsnew's parameters (days | since, by, project), or what's wrong with them. */
export function readNewsQuery(q: Record<string, unknown>): NewsQuery | { error: string } {
  const get = (k: string) => (typeof q[k] === "string" ? (q[k] as string) : Array.isArray(q[k]) ? String((q[k] as unknown[])[0]) : null);
  const p = readPeriod(get);
  if (p && "error" in p) return p;
  const by = get("by")?.trim() || "person";
  if (by !== "person" && by !== "area" && by !== "project") return { error: "by must be person, area or project." };
  const raw = get("project")?.trim().replace(/^#/, "");
  let project: number | null = null;
  if (raw) {
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1) return { error: "project must be a project's # number." };
    project = n;
  }
  return { period: p ?? DEFAULT_PERIOD, groupBy: by, project };
}

/** What GET /api/whatsnew answers: the brief, and the period it covers. */
export interface News {
  brief: Brief;
  period: Period;
  /** "Since last working day (Fri Oct 2)" */
  label: string;
  from: string;
  to: string;
  /** the board's time zone: dates on the page are written in it */
  timeZone: string;
  /** the last meeting someone marked (the "Since last meeting" choice shows while there is one) */
  meeting: { at: string; by: string } | null;
}
